import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Question } from 'src/app/models/question.model';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  Answer,
  AnswerWithStatus,
  EAnswerStatus,
  Expense,
} from 'src/app/models/Answer.model';
import { AnswersStore } from 'src/app/store/answers-store';
import { map, tap } from 'rxjs';
import { ExpenseItemDisplayComponent } from './expense-item-display/expense-item-display.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { QuestionnaireStore } from 'src/app/store/questionnaire-store';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from 'src/environment';

@Component({
    selector: 'que-expense-display',
    imports: [
        CommonModule,
        ExpenseItemDisplayComponent,
        ReactiveFormsModule,
        SvgIconComponent,
        TranslateDirective, TranslatePipe,
    ],
    templateUrl: './expense-display.component.html',
    styleUrls: ['./expense-display.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpenseDisplayComponent implements OnInit {
  @Input() isPreview = false;
  @Input() question?: Question;
  @Input() viewAnswer = false;

  #answerStore = inject(AnswersStore);
  #questionnaireStore = inject(QuestionnaireStore);
  #fb = inject(FormBuilder);

  canEditClientChargeFields = toSignal(
    this.#answerStore.canEditClientChargeFields$,
    { initialValue: false },
  );

  isNotUser$ = this.#questionnaireStore.isUser$.pipe(map((x) => !x));
  secondaryColor$ = this.#questionnaireStore.secondaryColor$;
  mainColor$ = this.#questionnaireStore.mainColor$;

  isUser?: boolean;

  answers = this.#fb.array<FormControl<Expense | null>>([]);
  currentAnswer?: Answer;

  amount$ = toSignal(this.#answerStore.amount$);
  maxAmount = environment.featurs.amountExpense;

  ngOnInit(): void {
    this.isNotUser$.subscribe((isNotUser) => {
      if (isNotUser) {
        this.isUser = false;
      } else {
        this.isUser = true;
      }
    });

    this.#answerStore.updateAnswerIsValid(
      this.answers.statusChanges
        .pipe(map((status) => status === 'VALID'))
        .pipe(tap((x) => console.log('isValidFROMComponent', x))),
    );

    this.answers.valueChanges.subscribe((answers) => {
      console.log('expense.valueChanges', answers);
      const updated = answers.find((a) => a?.status == EAnswerStatus.Changed);
      if (!updated) {
        return;
      }
      const updatedExpense: Expense = {
        ...updated,
        answerId: this.currentAnswer?.id ?? 0,
      };
      const expense = this.answers.controls
        .map((control, index) => {
          const value = control.value;

          if (!value) {
            return null;
          }
          return {
            ...value,
            answerId: this.currentAnswer?.id ?? 0,
            ModularRowNum: value.ModularRowNum ?? index + 1,
            status:
              value.ModularRowNum === updatedExpense.ModularRowNum
                ? EAnswerStatus.Saved
                : value.status,
          } as Expense;
        })
        .filter((x): x is Expense => x !== null);

      const answer: Answer = {
        id:  0,
        questionId: this.question?.id ?? 0,
        questionType: this.question?.type.id,
        expense,
        isValid: this.answers.valid,
      };

      this.currentAnswer = answer;

      this.#answerStore.updateAnswer(answer);

      this.answers.controls
        .find(
          (control) =>
            control.value?.ModularRowNum === updatedExpense.ModularRowNum,
        )
        ?.setValue(
          { ...updatedExpense, status: EAnswerStatus.Saved },
          { emitEvent: false },
        );

      this.updateAmount();
    });

    this.#answerStore.answers$
      .pipe(
        map((arr) => {
          const matches = arr.filter(
            (ans) => ans.questionId === this.question?.id,
          );

          if (matches.length <= 1) {
            return matches[0];
          }

          // הגנה מפני נתונים ישנים שבהם נוצר יותר ממיכל Answer אחד לאותה שאלה.
          // בלי האיחוד הזה find() היה מחזיר מיכל אחד בלבד, והמאשר היה רואה
          // חלק משורות ההוצאה - ומאשר סכום חלקי.
          return {
            ...matches[0],
            expense: matches
              .flatMap((ans) => ans.expense ?? [])
              .map((expense, index) => ({
                ...expense,
                ModularRowNum: index + 1,
              })),
          } as Answer;
        }),
      )
      .subscribe((answer) => {
        this.currentAnswer = answer;

        this.answers.clear({ emitEvent: false });

        const rawExpenses = answer?.expense?.length
          ? answer.expense
          : [this.createEmptyExpense(1)];

        // When an approver opens the page, prefill approvedAmount with the
        // requested amountInILS for any expense that hasn't been approved
        // yet, so it's shown in the UI and persisted right away (the
        // approver can still change it afterwards).
        let approvedAmountDefaulted = false;
        const expenses = rawExpenses.map((expense) => {
          if (this.canEditClientChargeFields() && !expense.approvedAmount) {
            approvedAmountDefaulted = true;
            return { ...expense, approvedAmount: expense.amount };
          }
          return expense;
        });

        expenses.forEach((expense, index) => {
          this.answers.push(
            new FormControl<Expense | null>({
              ...expense,
              answerId: answer?.id ?? 0,
              ModularRowNum: expense.ModularRowNum ?? index + 1,
              status: EAnswerStatus.Saved,
            }),
            { emitEvent: false },
          );
        });
        this.updateAmount();

        if (approvedAmountDefaulted) {
          const updatedAnswer: Answer = {
            id: 0,
            questionId: this.question?.id ?? 0,
            questionType: this.question?.type.id,
            expense: expenses,
            isValid: this.answers.valid,
          };

          this.currentAnswer = updatedAnswer;

          // Persist the defaulted approvedAmount without racing the initial
          // load (see AnswersStore.defaultApprovedAmounts for why this can't
          // be a plain updateAnswer() call here).
          this.#answerStore.defaultApprovedAmounts(updatedAnswer);
        }
      })
      .unsubscribe();
  }
  private updateAmount(): void {
    this.#answerStore.updateAmount(
      this.answers.value
        .map((x) => x?.amount ?? 0)
        .reduce((sum, value) => sum + value, 0),
    );
  }
  createEmptyExpense(row: number): Expense {
    return {
      id: 0,
      answerId: this.currentAnswer?.id ?? 0,
      amount: 0,
      expenseType: null,
      date: null,
      description: null,
      documentNumber: null,
      files: [],
      ModularRowNum: row,
      status: EAnswerStatus.Created,
      clientCharge: false,
      fullcharge: false,
      clientChargeAmount: 0,
      approvedAmount: 0,
      commentAproval: null,
    };
  }

  addExpense() {
    const newExpense = this.createEmptyExpense(this.answers.length + 1);
    this.answers.push(new FormControl<Expense>(newExpense));
  }

  validateAmount(): boolean {
    // הוצאות חו"ל אינן כפופות לתקרת הסכום.
    const foreignExpenseGuids = new Set<string | null | undefined>(
      this.question?.optionsList?.answerOptionItem
        ?.filter((o) => o.name === 'הוצאות חו"ל')
        .map((o) => o.guid),
    );
    const amount = this.answers.value
      // כל עוד סוג ההוצאה לא הוגדר עדיין, אי אפשר לדעת אם היא תוחרג מהתקרה -
      // אין לספור את השורה עד שנבחר סוג עבורה.
      .filter((x) => !!x?.expenseType && !foreignExpenseGuids.has(x.expenseType))
      .map((x) => x?.amount ?? 0)
      .reduce((sum, value) => sum + value, 0);

    return amount <= this.maxAmount;
  }

  deleteExpense(index: number): void {
    if (!this.isUser) {
      return;
    }

    if (index === -1) {
      return;
    }

    this.answers.removeAt(index, { emitEvent: false });

    this.answers.controls.forEach((control, i) => {
      const expense = control.value;

      if (!expense) {
        return;
      }

      control.setValue(
        {
          ...expense,
          answerId: this.currentAnswer?.id ?? 0,
          ModularRowNum: i + 1,
          status: EAnswerStatus.Saved,
        },
        { emitEvent: false },
      );
    });

    const expense = this.answers.controls
      .map((control) => control.value)
      .filter((x): x is Expense => x !== null);

    const answer: Answer = {
      id:  0,
      questionId: this.question?.id ?? 0,
      questionType: this.question?.type.id,
      expense,
      isValid: this.answers.valid,
    };

    this.currentAnswer = answer;
    this.#answerStore.updateAnswer(answer);
    this.updateAmount();
  }
}
